<script setup lang="ts">
const audioTag = ref<HTMLMediaElement | null>(null)
const seekbar = ref<HTMLElement | null>(null)

const props = defineProps({
  src: {
    type: String,
    required: true
  },
  img: {
    type: String,
    required: false,
    default: '/storage/landings/setar/image/audio-bg.jpg'
  },
  title: String,
  downloadable: {
    type: Boolean,
    default: true
  }
})

const state = reactive({
  play: false,
  duration: 0,
  currentTime: 0,
  timer: `۰۰:۰۰`
})

const isDragging = ref(false)
const dragPercent = ref(0)

const progressPercent = computed(() => {
  if (isDragging.value) return dragPercent.value
  if (!state.duration) return 0
  return (state.currentTime / state.duration) * 100
})

const numberFormat = new Intl.NumberFormat('fa-IR', { minimumIntegerDigits: 2 })

watch(
  () => state.play,
  (to) => {
    if (to === true) audioTag.value?.play()
    else audioTag.value?.pause()
  }
)

const updateTime = (e: any) => {
  if(!audioTag.value) return
  state.currentTime = audioTag.value.currentTime
  const percent = audioTag.value.currentTime / audioTag.value.duration

  if(percent === 1) {
    state.play = false
  }

  formatDuration(audioTag.value.currentTime)
}

const formatDuration = (time: number) => {
  if (typeof time !== 'number') return

  const seconds = Math.floor(time % 60)
  const minutes = Math.floor(time / 60) % 60
  const hours = Math.floor(time / 3500)

  if (hours === 0) state.timer = `${numberFormat.format(minutes)}:${numberFormat.format(seconds)}`
  else state.timer = `${numberFormat.format(hours)}:${numberFormat.format(minutes)}:${numberFormat.format(seconds)}`
}

const togglePlay = () => {
  state.play = !state.play
}

const startDrag = (e: MouseEvent | TouchEvent) => {
  isDragging.value = true
  updateDrag(e)

  window.addEventListener("mousemove", updateDrag)
  window.addEventListener("touchmove", updateDrag, { passive: false })
  window.addEventListener("mouseup", endDrag)
  window.addEventListener("touchend", endDrag)
}

const updateDrag = (e: MouseEvent | TouchEvent) => {
  if (!seekbar.value) return

  const rect = seekbar.value.getBoundingClientRect()

  let clientX: number
  if ("touches" in e && e.touches && e.touches[0]) {
    clientX = e.touches[0].clientX
  } else {
    clientX = (e as MouseEvent).clientX
  }

  let percent = ((clientX - rect.left) / rect.width) * 100
  percent = Math.min(100, Math.max(0, percent))

  dragPercent.value = percent

  if (isDragging.value && audioTag.value) {
    const newTime = (percent / 100) * state.duration
    audioTag.value.currentTime = newTime
  }
}

const endDrag = () => {
  isDragging.value = false

  window.removeEventListener("mousemove", updateDrag)
  window.removeEventListener("touchmove", updateDrag)
  window.removeEventListener("mouseup", endDrag)
  window.removeEventListener("touchend", endDrag)
}

onMounted(() => {
  if (audioTag.value?.duration) {
    state.duration = audioTag.value.duration
    formatDuration(audioTag.value.duration)
  }
})
</script>

<template>
  <div id="audio-container" class="w-full">
    <audio ref="audioTag" preload="metadata" :src="src" class="hidden" @timeupdate="updateTime" @loadedmetadata="state.duration = audioTag?.duration || 0"></audio>

    <div class="mx-auto flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6">
      <NuxtImg
        :src="props.img" 
        class="size-28 rounded-xs object-cover"
        :alt="title"
      />
  
      <div class="flex flex-col-reverse sm:flex-col items-center sm:items-start grow gap-4 w-full">
        <div class="flex flex-row-reverse w-full items-center gap-2.5">
          <UButton
            square
            size="xl"
            color="neutral"
            variant="ghost"
            :icon="state.play ? 'i-solar-pause-bold' : 'i-solar-play-bold'"
            @click="togglePlay"
          />
      
          <!-- Seekbar -->
          <div
            style="direction: ltr"
            ref="seekbar"
            class="relative w-full h-4 flex items-center cursor-pointer select-none rounded-full"
            @mousedown="startDrag"
            @touchstart.prevent="startDrag"
          >
            <div class="w-full h-1.5 bg-accented rounded-full relative">
              <!-- Played progress -->
              <div
                class="absolute h-1.5 bg-secondary rounded-full"
                :style="{ width: progressPercent + '%' }"
              ></div>
      
              <!-- Thumb -->
              <div
                class="absolute -top-1.5 h-4 w-4 bg-secondary rounded-full shadow-sm"
                :style="{ left: `calc(${progressPercent}% - 0.5rem)` }"
              ></div>
            </div>
          </div>
      
          <span class="small text-end w-10 shrink-0">{{ state.timer }}</span>
        </div>
    
        <div class="flex items-center justify-between">
          <p class="small">{{ title }}</p>
          <UButton
            v-if="downloadable"
            label="دانلود این قطعه"
            size="sm"
            color="neutral"
            variant="ghost"
            icon="i-solar-download-minimalistic-linear"
            :to="src"
            download
          />
        </div>
      </div>
    </div>
  </div>
</template>